import { Badge, Box, IconButton, Popover, Tooltip, Typography } from '@mui/material';
import { red } from '@mui/material/colors';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { NotificationsIcon } from 'app/components/Icons';
import * as Actions from 'app/store/actions';
import { useDispatch, useSelector } from 'app/modules/react-redux';
import { getProfile, getUserInboxSelector } from 'app/store/reducers';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type InboxItem } from 'app/store/types';
import { useLocation } from 'react-router';
import { getInboxNotificationSubject, getInboxNotificationMessage } from 'app/utils/inboxMessageHelpers';
import _ from '@lodash';
import errorMessages from '../../main/mfpDashboard/components/errorMessages';

const useStyles = makeStyles((theme) => ({
	notificationPopup: {
		color: theme.palette.common.white,
		backgroundColor: '#0870F8',
		width: 'calc(100vw - 280px - 20px)',
		padding: '2.8rem 1.2rem 2.8rem 9.6rem',
		'& button.MuiIconButton-root': {
			height: 32,
			width: 32,
			marginRight: 16,
		},
	},
	errorNotificationPopup: {
		color: theme.palette.common.white,
		backgroundColor: red[500],
	},
	notificationIcon: {
		fontSize: '4.8rem',
		position: 'absolute',
		top: '50%',
		left: '4.8rem',
		transform: 'translate(-50%, -50%)',
	},
	closeButtonWrapper: {
		position: 'absolute',
		top: 0,
		right: 0,
		margin: '0.2rem',
		marginTop: 12,
	},
}));

const Notification = () => {
	const { t } = useTranslation();
	const { t: tI } = useTranslation('inbox');
	const { t: deviceStatusT } = useTranslation('deviceStatus');
	const userInboxById = useSelector(getUserInboxSelector);
	const dispatch = useDispatch();
	const classes = useStyles();

	const [activeNotificationIndex, setActiveNotificationIndex] = React.useState(0);

	const {
		appData: { lastAcknowledgedNotificationDate },
	} = useSelector(getProfile);

	const saveLastAcknowledgedNotificationDate = (timestamp: InboxItem['createdAt']) => {
		dispatch(Actions.editUserAppData({ lastAcknowledgedNotificationDate: timestamp }));
	};

	const validInboxNotifications = Object.values(userInboxById ?? {})
		?.sort((a, b) => b.createdAt - a.createdAt)
		?.filter(({ type }) => type === 'error');
	const lastAcknowledgedNotificationIndex = (() => {
		if (lastAcknowledgedNotificationDate) {
			const index = validInboxNotifications?.findIndex(
				({ createdAt }) => createdAt <= lastAcknowledgedNotificationDate,
			);
			return index === -1 ? Infinity : index;
		}
		return Infinity;
	})();

	const unacknowledgedNotifications = validInboxNotifications?.slice(0, lastAcknowledgedNotificationIndex);

	const { pathname } = useLocation();

	useEffect(() => {
		dispatch(Actions.getInbox());
	}, [dispatch, pathname]);

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		saveLastAcknowledgedNotificationDate(unacknowledgedNotifications[0].createdAt);
	};

	type DeviceInfo = { deviceName: string; deviceSerial: string; fields: string };

	const getDeviceInfo = (rawDataString: string): DeviceInfo => {
		try {
			if (rawDataString && rawDataString.startsWith('{')) {
				const deviceInfo = JSON.parse(rawDataString);
				const deviceName = typeof deviceInfo.deviceName === 'string' ? deviceInfo.deviceName : null;
				const deviceSerial = typeof deviceInfo.deviceSerial === 'string' ? deviceInfo.deviceSerial : null;
				const fields = typeof deviceInfo.fields === 'string' ? deviceInfo.fields : null;

				if (deviceName || deviceSerial || fields) {
					return {
						deviceName: deviceName ?? 'Unknown Device',
						deviceSerial: deviceSerial ?? 'Unknown Serial',
						fields: fields ?? '',
					};
				}
			}
		} catch (e) {
			console.log('JSON parsing failed');
		}

		return {
			deviceName: 'Unknown Device',
			deviceSerial: 'Unknown Serial',
			fields: '',
		};
	};

	const getUniversalPrintInfo = (rawDataString: string) => {
		try {
			if (rawDataString && rawDataString.startsWith('{')) {
				const deviceInfo = JSON.parse(rawDataString);
				const deviceName = typeof deviceInfo.deviceName === 'string' ? deviceInfo.deviceName : null;
				const errorMessage = typeof deviceInfo.errorMessage === 'string' ? deviceInfo.errorMessage : null;

				if (deviceName && errorMessage) {
					return {
						deviceName: deviceName ?? 'Unknown Device',
						errorMessage,
					};
				}
			}
		} catch (e) {
			console.log('JSON parsing failed');
		}

		return {
			deviceName: 'Unknown Device',
			errorMessage: '',
		};
	};

	const getMfpTranslation = (key: string, origin: any): string => {
		if (!origin || !origin.name || typeof origin.name !== 'string') {
			return tI(key);
		}
		const { name } = origin;
		const { deviceName, deviceSerial, fields } = getDeviceInfo(name);
		if (isMfpDashOffline) {
			return t('inbox:mfpdash:offline', { deviceName, deviceSerial });
		}
		if (isMfpDashError) {
			let data: {
				Printer?: {
					Status?: string;
					Detail?: string;
				};
				Scanner?: {
					Status?: string;
					Detail?: string;
				};
			} = {};
			if (fields && fields.trim() !== '') {
				try {
					const parsed = JSON.parse(fields);
					data = parsed ?? {};
				} catch {
					data = {};
				}
			}
			const hasComponentError = (status?: string) =>
				status && (status === 'Error1' || status === 'Error2' || status === 'Fatal');

			let deviceComponentErrorString = '';
			let error = '';
			const printerError = deviceStatusT(errorMessages[data?.Printer?.Detail ?? '']);
			const scannerError = deviceStatusT(errorMessages[data?.Scanner?.Detail ?? '']);
			if (hasComponentError(data?.Printer?.Status) && hasComponentError(data?.Scanner?.Status)) {
				deviceComponentErrorString = t('inbox:mfpdash:error:printer scanner');
				error = `${printerError}, ${scannerError}`;
			} else if (hasComponentError(data?.Printer?.Status)) {
				deviceComponentErrorString = t('inbox:mfpdash:error:printer');
				error = printerError;
			} else if (hasComponentError(data?.Scanner?.Status)) {
				deviceComponentErrorString = t('inbox:mfpdash:error:scanner');
				error = scannerError;
			}
			return t('inbox:mfpdash:error', { deviceName, deviceSerial, deviceComponentErrorString, error });
		}
		if (isMfpDashEmptyPaper) {
			return t('inbox:mfpdash:emptypaper', { deviceName, deviceSerial });
		}
		if (isMfpDashLowToner) {
			return t('inbox:mfpdash:lowtoner', { deviceName, deviceSerial });
		}

		return tI(key);
	};

	const getUnivPrintTranslation = (key: string, origin: any): string => {
		if (!origin || !origin.name || typeof origin.name !== 'string') {
			return tI(key);
		}
		const { name } = origin;
		const { deviceName, errorMessage } = getUniversalPrintInfo(name);

		return t('inbox:universal print:registration failed', { deviceName });
	};

	const open = !!anchorEl;

	if (unacknowledgedNotifications.length === 0) {
		return (
			<Tooltip title={t('notification:no new notifications')}>
				<span>
					<IconButton
						className="w-64 h-64 starter:text-white landing-toolbar-text-white"
						disabled
						size="large"
					>
						<NotificationsIcon style={{ maxWidth: 24 }} />
					</IconButton>
				</span>
			</Tooltip>
		);
	}

	const activeNotification = unacknowledgedNotifications[activeNotificationIndex];

	const isMfpDashOffline = activeNotification.messageKey === 'mfp-dash-offline';
	const isMfpDashError = activeNotification.messageKey === 'mfp-dash-error';
	const isMfpDashEmptyPaper = activeNotification.messageKey === 'mfp-dash-empty-paper';
	const isMfpDashLowToner = activeNotification.messageKey === 'mfp-dash-low-toner';
	const isMfpDash = isMfpDashOffline || isMfpDashError || isMfpDashEmptyPaper || isMfpDashLowToner;

	const isUniversalPrintAdminError = activeNotification.messageKey === 'forbidden';
	const isUniversalPrintGenericError = activeNotification.messageKey === 'generic_error';
	const isAzureDeviceAlreadyExistsError = activeNotification.messageKey === 'device_already_exists';
	const isAzureRegistrationIdInvalidError = activeNotification.messageKey === 'invalid_registration_id';
	const isAzureUserTokenError = activeNotification.messageKey === 'user_token_error';
	const isAzureStorageError = activeNotification.messageKey === 'storage_error';
	const isAzureServiceError = activeNotification.messageKey === 'service_error';

	const isUniversalPrintError =
		isUniversalPrintAdminError ||
		isUniversalPrintGenericError ||
		isAzureDeviceAlreadyExistsError ||
		isAzureRegistrationIdInvalidError ||
		isAzureUserTokenError ||
		isAzureStorageError ||
		isAzureServiceError;
	return (
		<>
			<IconButton
				className="w-64 h-64 starter:text-white landing-toolbar-text-white"
				onClick={handleClick}
				size="large"
			>
				<Badge color="error" badgeContent={unacknowledgedNotifications.length}>
					<NotificationsIcon style={{ maxWidth: 24 }} />
				</Badge>
			</IconButton>
			<Popover
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
			>
				<div
					className={clsx(classes.notificationPopup, {
						[classes.errorNotificationPopup]: activeNotification.type === 'error',
					})}
				>
					{activeNotification.type === 'error' ? (
						<ErrorIcon className={classes.notificationIcon} />
					) : (
						<InfoIcon className={classes.notificationIcon} />
					)}
					<div className="mb-8 flex items-center">
						<Typography className="pr-16" variant="h6">
							{getInboxNotificationSubject(
								activeNotification.messageKey,
								tI,
								activeNotification.type === 'error'
									? activeNotification.origin.type === 'workflow'
										? t('notification:header:workflow error')
										: t('notification:header:error')
									: t('notification:header:info'),
								activeNotification.data,
							)}
						</Typography>
						<div
							className={clsx(
								{
									'opacity-0 pointer-events-none': unacknowledgedNotifications.length <= 1,
								},
								'flex items-center',
							)}
						>
							<Typography className="pr-16" variant="h6">
								<span className="font-normal">
									{activeNotificationIndex + 1}/{unacknowledgedNotifications.length}
								</span>
							</Typography>
							<div className="pr-16">
								<IconButton
									color="inherit"
									onClick={() => {
										setActiveNotificationIndex(
											(prevActiveNotification) => prevActiveNotification - 1,
										);
									}}
									disabled={activeNotificationIndex === 0}
									size="large"
								>
									<KeyboardArrowLeft />
								</IconButton>
								<IconButton
									color="inherit"
									onClick={() => {
										setActiveNotificationIndex(
											(prevActiveNotification) => prevActiveNotification + 1,
										);
									}}
									disabled={activeNotificationIndex === unacknowledgedNotifications.length - 1}
									size="large"
								>
									<KeyboardArrowRight />
								</IconButton>
							</div>
						</div>
					</div>
					<Box display="flex" justifyContent="space-between">
						<Typography style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
							{isMfpDash &&
								getMfpTranslation(activeNotification.messageKey ?? '', activeNotification.origin)}
							{isUniversalPrintError &&
								getUnivPrintTranslation(activeNotification.messageKey ?? '', activeNotification.origin)}
							{!isMfpDash &&
								!isUniversalPrintError &&
								getInboxNotificationMessage(
									activeNotification.messageKey,
									tI,
									activeNotification.info,
									activeNotification.data,
								)}
						</Typography>
						{/* <Button className="ml-16" color="inherit" onClick={saveAcknowledgedNotifications}>
                        {t('notification:dismiss')}
                    </Button> */}
					</Box>
					<div className={classes.closeButtonWrapper}>
						<IconButton onClick={handleClose} color="inherit" size="large">
							<CloseIcon />
						</IconButton>
					</div>
				</div>
			</Popover>
		</>
	);
};

export default Notification;
